import React, { useEffect, useState } from 'react';
import { generateGoogleAdsSummary } from '../../services/geminiService';
import LoadingSpinner from '../shared/LoadingSpinner';
import { TONES, ICONS } from '../shared/tones';
import { fmtCount } from '../shared/format';

// ROAS bands: < 1.0x burning cash, 1.0–2.0x weak, 2.0–4.0x healthy, > 4.0x strong.
// Bookings change pct: < -10% declining, -10..10% steady, > 10% growing.
export function computeAdsVerdict({ kpis, actionsCount = 0 } = {}) {
  const roas = kpis?.roas != null ? Number(kpis.roas) : null;
  const spend = kpis?.spend != null ? Number(kpis.spend) : null;
  const bookings = kpis?.tier1_bookings != null ? Number(kpis.tier1_bookings) : null;
  const bookingsPct = kpis?.comparison?.bookings_change_pct;
  const roasPct = kpis?.comparison?.roas_change_pct;

  const cta = actionsCount > 0
    ? { label: `Review ${actionsCount} campaign${actionsCount === 1 ? '' : 's'}`, href: '#campaign-performance' }
    : null;

  if (roas == null || (spend != null && spend === 0)) {
    return {
      tone: 'gray',
      icon: 'compass',
      label: 'Insufficient data',
      headline: 'Not enough spend or conversion data to score this period.',
      subline: 'A verdict appears once Google Ads reports both spend and conversion value.',
      cta: null,
    };
  }

  const roasStrong = roas >= 4;
  const roasHealthy = roas >= 2 && roas < 4;
  const roasWeak = roas >= 1 && roas < 2;
  const roasBurning = roas < 1;

  const bookingsGrowing = bookingsPct != null && bookingsPct > 10;
  const bookingsDeclining = bookingsPct != null && bookingsPct < -10;
  const roasImproving = roasPct != null && roasPct > 5;
  const roasDeclining = roasPct != null && roasPct < -5;

  if (roasStrong && !bookingsDeclining) {
    return {
      tone: 'emerald',
      icon: 'trophy',
      label: 'Winning',
      headline: `Earning $${roas.toFixed(2)} per $1 spent${bookings != null ? ` across ${fmtCount(bookings)} bookings` : ''}.`,
      subline: roasImproving
        ? `ROAS improved ${roasPct.toFixed(1)}% — there is room to scale spend on the strongest campaigns.`
        : 'Strong returns. Consider scaling spend on the highest-ROAS campaigns to capture more demand.',
      cta,
    };
  }

  if (roasHealthy && bookingsGrowing) {
    return {
      tone: 'emerald',
      icon: 'trophy',
      label: 'Growing',
      headline: `Bookings up ${bookingsPct.toFixed(1)}% with ROAS at ${roas.toFixed(2)}x.`,
      subline: 'Account is profitable and trending up. Watch CPA on new campaigns to keep efficiency.',
      cta,
    };
  }

  if (roasBurning) {
    return {
      tone: 'red',
      icon: 'bolt',
      label: 'Act now',
      headline: `ROAS is ${roas.toFixed(2)}x — every $1 spent returns less than $1.`,
      subline: 'Pause weakest campaigns, tighten negative keywords, and shift budget to your top-converting ad groups.',
      cta,
    };
  }

  if (roasWeak && bookingsDeclining) {
    return {
      tone: 'red',
      icon: 'bolt',
      label: 'Reposition',
      headline: `ROAS at ${roas.toFixed(2)}x and bookings down ${Math.abs(bookingsPct).toFixed(1)}%.`,
      subline: 'Efficiency and volume are both slipping — review keyword match types, landing pages, and bid strategy.',
      cta,
    };
  }

  if (roasDeclining) {
    return {
      tone: 'amber',
      icon: 'alert',
      label: 'Watch',
      headline: `ROAS dropped ${Math.abs(roasPct).toFixed(1)}% to ${roas.toFixed(2)}x.`,
      subline: 'Returns are softening. Check for rising CPCs, new competitors, or seasonality before scaling further.',
      cta,
    };
  }

  if (roasWeak) {
    return {
      tone: 'amber',
      icon: 'alert',
      label: 'Watch',
      headline: `ROAS is ${roas.toFixed(2)}x — margins are thin.`,
      subline: 'Tighten targeting and lower bids on underperforming keywords before adding more budget.',
      cta,
    };
  }

  return {
    tone: 'gray',
    icon: 'compass',
    label: 'Steady',
    headline: `ROAS at ${roas.toFixed(2)}x with stable bookings.`,
    subline: 'No urgent moves. Keep monitoring CTR and cost per booking for early shifts.',
    cta,
  };
}

export default function VerdictCard({ kpis, actionsCount = 0, clientId, startDate, endDate, comparisonMode = 'previous_period' }) {
  const [dismissed, setDismissed] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const verdict = computeAdsVerdict({ kpis, actionsCount });
  const tone = TONES[verdict.tone] ?? TONES.gray;

  // The service caches on `gads|clientId|startDate|endDate|comparisonMode|verdictLabel`,
  // so the effect only needs those primitives plus `verdict.label` (which keys
  // the cache and is itself derived from kpis). Depending on the `kpis` object
  // would re-fire the paid Gemini call on every parent render that hands down a
  // new reference. `kpis` is read inside the closure; `verdict.headline`/`.subline`
  // are pure functions of the same inputs as `verdict.label`, so they're omitted.
  // `roasReady` is the single primitive gate. (Same fix as the CI VerdictCard.)
  const roasReady = !!kpis && kpis.roas != null;
  useEffect(() => {
    if (!clientId || !startDate || !endDate || !roasReady) {
      setAiText(null);
      return;
    }
    let cancelled = false;
    setAiLoading(true);
    const fallbackText = `${verdict.headline} ${verdict.subline}`;
    generateGoogleAdsSummary({
      clientId, startDate, endDate, kpis, comparisonMode,
      verdictLabel: verdict.label,
      fallbackText,
    })
      .then((res) => {
        if (!cancelled) {
          setAiText(res.success ? res.data : null);
          setAiLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAiText(null);
          setAiLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [clientId, startDate, endDate, comparisonMode, verdict.label, roasReady]); // eslint-disable-line

  if (dismissed) return null;

  return (
    <div
      role="region"
      aria-label="Google Ads top insight"
      className={`relative ${tone.bg} border ${tone.border} rounded-xl p-4`}
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <svg className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {ICONS[verdict.icon] ?? ICONS.compass}
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Top Insight — {verdict.label}
          </h3>
          {aiLoading && !aiText ? (
            <LoadingSpinner label="Analyzing performance..." inline />
          ) : aiText ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{aiText}</p>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <span className="font-medium text-gray-900 dark:text-gray-100">{verdict.headline}</span>{' '}
              {verdict.subline}
            </p>
          )}
          {verdict.cta && (
            <div className={`flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t ${tone.divider}`}>
              <a
                href={verdict.cta.href}
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span className={`w-2 h-2 rounded-full ${tone.dot} flex-shrink-0`} />
                {verdict.cta.label} →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
